import { useEffect, useState } from "react";
import { Button, Card, Cluster, FormField, Input, Stack, useToast } from "@salesapp/ui";
import { reportSaveFailure, reportSaved } from "../../lib/saveNotice";
import {
  crmApi,
  type BoardDisplayEntity,
  type BoardDisplaySettings,
} from "../../lib/crmApi";
import { ColorSwatchRow } from "./dashboard_designer/DesignerControls";
import { FIELD_HINTS } from "../../help/fieldHints";
import "./dashboard_designer/designers.css";

type Props = {
  orgId: string;
  entity: BoardDisplayEntity;
  onSaved?: (settings: BoardDisplaySettings) => void;
};

const HEX_RE = /^#[0-9a-f]{6}$/i;
const BOARD_SWATCH_GROUPS = [
  {
    label: "Board",
    options: [
      "var(--color-board-aging-1)",
      "var(--color-board-aging-2)",
      "var(--color-board-aging-3)",
      "var(--color-board-aging-4)",
      "var(--color-board-terminal-positive)",
      "var(--color-board-terminal-negative)",
    ],
  },
  {
    label: "Electric",
    options: [
      "var(--color-chart-scheme-pbi-electric-1)",
      "var(--color-chart-scheme-pbi-electric-2)",
      "var(--color-chart-scheme-pbi-electric-3)",
      "var(--color-chart-scheme-pbi-electric-4)",
      "var(--color-chart-scheme-pbi-electric-5)",
      "var(--color-chart-scheme-pbi-electric-6)",
      "var(--color-chart-scheme-pbi-electric-7)",
      "var(--color-chart-scheme-pbi-electric-8)",
    ],
  },
];

function normalizeBoardColor(value: string): string {
  const trimmed = value.trim();
  if (/^var\(--color-(?:board|chart-scheme-pbi-electric)-[a-z0-9-]+\)$/.test(trimmed)) return trimmed;
  const lower = trimmed.toLowerCase();
  if (HEX_RE.test(lower)) return lower;
  return "#dff5e9";
}

function boardDisplayPayload(settings: BoardDisplaySettings): Omit<BoardDisplaySettings, "entity"> {
  return {
    retain_positive_days: settings.retain_positive_days,
    retain_negative_days: settings.retain_negative_days,
    aging_band1_max_days: settings.aging_band1_max_days,
    aging_band2_max_days: settings.aging_band2_max_days,
    aging_band3_max_days: settings.aging_band3_max_days,
    aging_band1_color: normalizeBoardColor(settings.aging_band1_color),
    aging_band2_color: normalizeBoardColor(settings.aging_band2_color),
    aging_band3_color: normalizeBoardColor(settings.aging_band3_color),
    aging_band4_color: normalizeBoardColor(settings.aging_band4_color),
    terminal_positive_color: normalizeBoardColor(settings.terminal_positive_color),
    terminal_negative_color: normalizeBoardColor(settings.terminal_negative_color),
  };
}

type Defaults = NonNullable<BoardDisplaySettings["defaults"]>;

const RETENTION_KEYS = ["retain_positive_days", "retain_negative_days"] as const;
const AGING_KEYS = [
  "aging_band1_max_days",
  "aging_band2_max_days",
  "aging_band3_max_days",
  "aging_band1_color",
  "aging_band2_color",
  "aging_band3_color",
  "aging_band4_color",
] as const;
const TERMINAL_KEYS = ["terminal_positive_color", "terminal_negative_color"] as const;

function ColorChip({ color }: { color: string }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: "var(--space-4)",
        height: "var(--space-4)",
        borderRadius: "var(--radius-sm)",
        border: "thin solid var(--color-border)",
        background: color,
        verticalAlign: "middle",
      }}
    />
  );
}

/** "Defaults: ..." line plus a button that puts the built-in values back into the form (applied on Save). */
function RestoreDefaults({
  label,
  summary,
  isDefault,
  onRestore,
}: {
  label: string;
  summary: React.ReactNode;
  isDefault: boolean;
  onRestore: () => void;
}) {
  return (
    <Stack gap={2}>
      <p className="crm-muted">
        Defaults: {summary}
        {isDefault && " (currently in use)"}
      </p>
      <div>
        <Button type="button" variant="secondary" onClick={onRestore} disabled={isDefault} aria-label={`Restore ${label} defaults`}>
          Restore defaults
        </Button>
      </div>
    </Stack>
  );
}

export function BoardDisplaySettingsPanel({ orgId, entity, onSaved }: Props) {
  const { show } = useToast();
  const isLead = entity === "lead";
  const [settings, setSettings] = useState<BoardDisplaySettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void crmApi
      .getBoardDisplay(orgId, entity)
      .then(setSettings)
      .catch((err) => setError(err instanceof Error ? err.message : "Settings could not be loaded."));
  }, [orgId, entity]);

  function patch(next: Partial<BoardDisplaySettings>) {
    setSettings((current) => (current ? { ...current, ...next } : current));
    setSaved(false);
  }

  async function save() {
    if (!settings) return;
    setSaving(true);
    setError(null);
    try {
      const result = await crmApi.putBoardDisplay(orgId, entity, boardDisplayPayload(settings));
      setSettings(result);
      setSaved(true);
      reportSaved(show);
      onSaved?.(result);
    } catch (err) {
      setError(reportSaveFailure(show, err, "Settings could not be saved."));
    } finally {
      setSaving(false);
    }
  }

  if (!settings) {
    return <p className="home-subtitle">{error ?? "Loading board settings…"}</p>;
  }

  const defaults: Defaults | undefined = settings.defaults;
  const sectionIsDefault = (keys: readonly (keyof Defaults)[]) =>
    !defaults || keys.every((key) => settings[key] === defaults[key]);
  const restore = (keys: readonly (keyof Defaults)[]) => {
    if (!defaults) return;
    patch(Object.fromEntries(keys.map((key) => [key, defaults[key]])) as Partial<BoardDisplaySettings>);
  };
  const daysLabel = (n: number) => `${n} ${n === 1 ? "day" : "days"}`;

  return (
    <Stack gap={4}>
      {error ? <p className="crm-error">{error}</p> : null}
      <p className="home-subtitle">
        {isLead
          ? "Control how long Converted and Disqualified leads stay on the board, and the colours used for open-card aging and terminal stages."
          : "Control how long Closed Won and Closed Lost deals stay on the board, and the colours used for open-card aging and terminal stages."}
      </p>

      <Card>
        <Stack gap={3}>
          <h2 className="crm-section-title">Board retention</h2>
          <FormField
            label={isLead ? "Converted stays on board (days)" : "Closed Won stays on board (days)"}
            hint="1 keeps cards for the rest of their event day (default). 0 hides on the next sweep."
            tooltip={FIELD_HINTS.board_retain_days}
          >
            <Input
              type="number"
              min={0}
              value={String(settings.retain_positive_days)}
              onChange={(event) => patch({ retain_positive_days: Number(event.target.value) })}
            />
          </FormField>
          <FormField
            label={isLead ? "Disqualified stays on board (days)" : "Closed Lost stays on board (days)"}
            hint="Same calendar-day rules as above."
            tooltip={FIELD_HINTS.board_retain_days}
          >
            <Input
              type="number"
              min={0}
              value={String(settings.retain_negative_days)}
              onChange={(event) => patch({ retain_negative_days: Number(event.target.value) })}
            />
          </FormField>
          {defaults && (
            <RestoreDefaults
              label="board retention"
              summary={`${daysLabel(defaults.retain_positive_days)} (${isLead ? "Converted" : "Closed Won"}), ${daysLabel(defaults.retain_negative_days)} (${isLead ? "Disqualified" : "Closed Lost"})`}
              isDefault={sectionIsDefault(RETENTION_KEYS)}
              onRestore={() => restore(RETENTION_KEYS)}
            />
          )}
        </Stack>
      </Card>

      <Card>
        <Stack gap={3}>
          <h2 className="crm-section-title">Aging colours (open cards)</h2>
          <p className="home-subtitle">
            Four bands based on card age. Thresholds are exclusive upper bounds for each band until the last.
          </p>
          <FormField label="Band 1 under (days)" tooltip={FIELD_HINTS.board_aging_band_days}>
            <Input
              type="number"
              min={1}
              value={String(settings.aging_band1_max_days)}
              onChange={(event) => patch({ aging_band1_max_days: Number(event.target.value) })}
            />
          </FormField>
          <ColorSwatchRow
            label="Band 1 colour"
            value={settings.aging_band1_color}
            swatchGroups={BOARD_SWATCH_GROUPS}
            tooltip={FIELD_HINTS.board_aging_band_color}
            onChange={(aging_band1_color) => patch({ aging_band1_color })}
          />
          <FormField label="Band 2 under (days)" tooltip={FIELD_HINTS.board_aging_band_days}>
            <Input
              type="number"
              min={1}
              value={String(settings.aging_band2_max_days)}
              onChange={(event) => patch({ aging_band2_max_days: Number(event.target.value) })}
            />
          </FormField>
          <ColorSwatchRow
            label="Band 2 colour"
            value={settings.aging_band2_color}
            swatchGroups={BOARD_SWATCH_GROUPS}
            tooltip={FIELD_HINTS.board_aging_band_color}
            onChange={(aging_band2_color) => patch({ aging_band2_color })}
          />
          <FormField label="Band 3 under (days)" tooltip={FIELD_HINTS.board_aging_band_days}>
            <Input
              type="number"
              min={1}
              value={String(settings.aging_band3_max_days)}
              onChange={(event) => patch({ aging_band3_max_days: Number(event.target.value) })}
            />
          </FormField>
          <ColorSwatchRow
            label="Band 3 colour"
            value={settings.aging_band3_color}
            swatchGroups={BOARD_SWATCH_GROUPS}
            tooltip={FIELD_HINTS.board_aging_band_color}
            onChange={(aging_band3_color) => patch({ aging_band3_color })}
          />
          <ColorSwatchRow
            label="Band 4 colour (at or above band 3)"
            value={settings.aging_band4_color}
            swatchGroups={BOARD_SWATCH_GROUPS}
            tooltip={FIELD_HINTS.board_aging_band_color}
            onChange={(aging_band4_color) => patch({ aging_band4_color })}
          />
          {defaults && (
            <RestoreDefaults
              label="aging colours"
              summary={
                <>
                  <ColorChip color={defaults.aging_band1_color} /> under {daysLabel(defaults.aging_band1_max_days)},{" "}
                  <ColorChip color={defaults.aging_band2_color} /> under {daysLabel(defaults.aging_band2_max_days)},{" "}
                  <ColorChip color={defaults.aging_band3_color} /> under {daysLabel(defaults.aging_band3_max_days)},{" "}
                  <ColorChip color={defaults.aging_band4_color} /> beyond
                </>
              }
              isDefault={sectionIsDefault(AGING_KEYS)}
              onRestore={() => restore(AGING_KEYS)}
            />
          )}
        </Stack>
      </Card>

      <Card>
        <Stack gap={3}>
          <h2 className="crm-section-title">Terminal stage colours</h2>
          <p className="home-subtitle">Solid background only — aging does not apply to these stages.</p>
          <ColorSwatchRow
            label={isLead ? "Converted" : "Closed Won"}
            value={settings.terminal_positive_color}
            swatchGroups={BOARD_SWATCH_GROUPS}
            tooltip={FIELD_HINTS.board_terminal_color}
            onChange={(terminal_positive_color) => patch({ terminal_positive_color })}
          />
          <ColorSwatchRow
            label={isLead ? "Disqualified" : "Closed Lost"}
            value={settings.terminal_negative_color}
            swatchGroups={BOARD_SWATCH_GROUPS}
            tooltip={FIELD_HINTS.board_terminal_color}
            onChange={(terminal_negative_color) => patch({ terminal_negative_color })}
          />
          {defaults && (
            <RestoreDefaults
              label="terminal stage colours"
              summary={
                <>
                  <ColorChip color={defaults.terminal_positive_color} /> {isLead ? "Converted" : "Closed Won"},{" "}
                  <ColorChip color={defaults.terminal_negative_color} /> {isLead ? "Disqualified" : "Closed Lost"}
                </>
              }
              isDefault={sectionIsDefault(TERMINAL_KEYS)}
              onRestore={() => restore(TERMINAL_KEYS)}
            />
          )}
        </Stack>
      </Card>

      <Cluster gap={2}>
        <Button type="button" onClick={() => save()} disabled={saving}>
          {saving ? "Saving…" : "Save board settings"}
        </Button>
        {saved ? <span className="crm-muted">Saved</span> : null}
      </Cluster>
    </Stack>
  );
}
